import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { PillSearchBar } from "@/components/common/PillSearchBar";
import { FloatingControlsBar } from "@/components/projects/FloatingControlsBar";
import { ProjectRow } from "@/components/projects/ProjectRow";
import { ProjectSection } from "@/components/projects/ProjectSection";
import { useDealStages } from "@/contexts/DealStagesContext";
import { useArchiveData } from "@/hooks/useArchiveData";
import { useOffices } from "@/hooks/useOffices";
import { useUserTimeZone } from "@/hooks/useUserTimeZone";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import {
  calendarDateInTimeZone,
  computeProjectEndDate,
  currentWeekStartInTimeZone,
  toISODateString,
} from "@/utils/dateUtils";
import { filterProjectsForBoard } from "@/utils/projectUtils";
import { FoldVertical, UnfoldVertical } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

function categorizeArchivedProjects(projects: ProjectSummary[], today: string) {
  const lost: ProjectSummary[] = [];
  const completed: ProjectSummary[] = [];

  for (const project of projects) {
    if (project.deal_stage?.kind === "lost") {
      lost.push(project);
    } else {
      const endDate = computeProjectEndDate(project.start_date, project.num_weeks);
      if (endDate < today) {
        completed.push(project);
      }
    }
  }

  return { lost, completed };
}

interface ArchivedProjectsTabProps {
  // Rendered as the first child of the board (the page-level tab switcher).
  header?: ReactNode;
}

/**
 * "Archived" tab - completed and lost projects, with the same sticky/floating
 * search controls as the Current tab.
 */
export function ArchivedProjectsTab({ header }: ArchivedProjectsTabProps) {
  const { projects, loading, error, refresh, updateProjectOptimistically, refreshSilently } = useArchiveData();
  const { selectedOffices } = useOffices();
  const { wonStage, lostStage } = useDealStages();
  const userTimeZone = useUserTimeZone();

  const [searchPills, setSearchPills] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});

  const today = useMemo(() => {
    return toISODateString(calendarDateInTimeZone(new Date(), userTimeZone));
  }, [userTimeZone]);
  const timelineStartDate = useMemo(
    () => toISODateString(currentWeekStartInTimeZone(userTimeZone)),
    [userTimeZone],
  );

  const expandAllProjects = useCallback(() => {
    setExpandedProjects(Object.fromEntries(projects.map((p) => [p.id, true])));
  }, [projects]);

  const collapseAllProjects = useCallback(() => {
    setExpandedProjects({});
  }, []);

  const expandProject = useCallback((projectId: string) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  }, []);

  const activeSearchTerms = useMemo(
    () => [...searchPills, ...(searchInput.trim() ? [searchInput.trim()] : [])],
    [searchPills, searchInput],
  );

  const filteredProjects = useMemo(() => {
    return filterProjectsForBoard(projects, activeSearchTerms, selectedOffices);
  }, [projects, activeSearchTerms, selectedOffices]);

  const grouped = useMemo(
    () => categorizeArchivedProjects(filteredProjects, today),
    [filteredProjects, today]
  );

  const overAllocatedEmployeeIds = useMemo(() => new Set<string>(), []);

  return (
    <BoardLayout>
      {header}
      <FloatingControlsBar>
        <div className="board-controls__search-row">
          <PillSearchBar
            pills={searchPills}
            onPillsChange={setSearchPills}
            inputValue={searchInput}
            onInputChange={setSearchInput}
            placeholder="Search archived projects..."
          />
          <div className="board-controls__expand-collapse">
            <span className="board-controls__expand-collapse-label">
              Expand/Collapse All Projects
            </span>
            <button
              type="button"
              className="board-controls__icon-button"
              onClick={expandAllProjects}
              title="Expand All Projects"
              aria-label="Expand All Projects"
            >
              <UnfoldVertical size={18} />
            </button>
            <button
              type="button"
              className="board-controls__icon-button"
              onClick={collapseAllProjects}
              title="Collapse All Projects"
              aria-label="Collapse All Projects"
            >
              <FoldVertical size={18} />
            </button>
          </div>
        </div>
      </FloatingControlsBar>
      {error && <ErrorBanner message={error} entityName="archived projects" />}
      {loading ? (
        <LoadingState message="Loading archived projects..." />
      ) : (
        <div className="board-content">
          <ProjectSection
            title="Completed"
            count={grouped.completed.length}
            infoText="Projects whose computed end date is before today"
            defaultDealStageId={wonStage?.id ?? ""}
            onSave={refresh}
          >
            {grouped.completed.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                timelineStartDate={timelineStartDate}
                onRefresh={refresh}
                updateProjectOptimistically={updateProjectOptimistically}
                refreshSilently={refreshSilently}
                overAllocatedEmployeeIds={overAllocatedEmployeeIds}
                expanded={!!expandedProjects[project.id]}
                onToggleExpanded={expandProject}
              />
            ))}
          </ProjectSection>
          <ProjectSection
            title="Lost"
            count={grouped.lost.length}
            infoText="Projects with deal stage 'Lost'"
            defaultDealStageId={lostStage?.id ?? ""}
            onSave={refresh}
          >
            {grouped.lost.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                timelineStartDate={timelineStartDate}
                onRefresh={refresh}
                updateProjectOptimistically={updateProjectOptimistically}
                refreshSilently={refreshSilently}
                overAllocatedEmployeeIds={overAllocatedEmployeeIds}
                expanded={!!expandedProjects[project.id]}
                onToggleExpanded={expandProject}
              />
            ))}
          </ProjectSection>
        </div>
      )}
    </BoardLayout>
  );
}
